import type { CategoryScore, ScorecardItem } from '../types';
import {
  type HierarchyNode,
  SCORECARD_HIERARCHY,
  getCategoriesInHierarchy,
} from '../components/ScorecardCategories/scorecardHierarchy';

export interface GroupedCategoryScore {
  name: string;
  displayName: string;
  type: 'group' | 'category';
  score: number | 'N/A';
  tier?: string;
  children?: GroupedCategoryScore[];
  categoryData?: CategoryScore;
}

const GROUP_COLORS_LIGHT: Record<string, string> = {
  standardization: '#4caf50',
  security: '#f44336',
  developer_productivity: '#2196f3',
  cost: '#ff9800',
  reliability: '#9c27b0',
  other: '#607d8b',
};

const GROUP_COLORS_DARK: Record<string, string> = {
  standardization: '#81c784',
  security: '#ef9a9a',
  developer_productivity: '#64b5f6',
  cost: '#ffb74d',
  reliability: '#ce93d8',
  other: '#90a4ae',
};

const getGroupColor = (name: string, isDark?: boolean): string => {
  const colors = isDark ? GROUP_COLORS_DARK : GROUP_COLORS_LIGHT;
  return colors[name.toLowerCase()] || (isDark ? '#ce93d8' : '#9c27b0');
};

const aggregateRecursive = (
  node: HierarchyNode,
  flatItems: ScorecardItem[],
  isDark?: boolean,
): ScorecardItem | null => {
  if (node.type === 'category') {
    return flatItems.find(item => item.label === node.categoryName) || null;
  }

  const childResults = (node.children || [])
    .map(child => aggregateRecursive(child, flatItems, isDark))
    .filter(
      (res): res is ScorecardItem =>
        res !== null && res.valueOverTime.length > 0,
    );

  if (childResults.length === 0) return null;

  const timePoints = childResults[0].valueOverTime.length;
  const aggregatedVot = Array.from({ length: timePoints }).map((_, idx) => {
    const date = childResults[0].valueOverTime[idx].date;
    const validValues = childResults
      .map(res => {
        const val = res.valueOverTime[idx]?.value;
        return typeof val === 'number'
          ? val
          : parseFloat((val as string) ?? '');
      })
      .filter(v => !isNaN(v));

    const average =
      validValues.length > 0
        ? validValues.reduce((a, b) => a + b, 0) / validValues.length
        : 0;

    return { date, value: parseFloat(average.toFixed(2)) };
  });

  return {
    label: node.displayName,
    valueOverTime: aggregatedVot,
    color: getGroupColor(node.name, isDark),
    checked: true,
  };
};

export const getAggregatedScorecardItems = (
  flatItems: ScorecardItem[] | null,
  hierarchy: HierarchyNode[],
  isDark?: boolean,
): ScorecardItem[] => {
  if (!flatItems || flatItems.length === 0) return [];

  const topLevelItems = hierarchy
    .map(rootNode => aggregateRecursive(rootNode, flatItems, isDark))
    .filter((item): item is ScorecardItem => item !== null);

  const categoriesInHierarchy = getCategoriesInHierarchy();
  const ungrouped = flatItems.filter(
    i => !categoriesInHierarchy.includes(i.label),
  );

  if (ungrouped.length > 0) {
    const timePoints = ungrouped[0].valueOverTime.length;
    const otherVot = Array.from({ length: timePoints }).map((_, idx) => {
      const date = ungrouped[0].valueOverTime[idx].date;
      const sum = ungrouped.reduce(
        (acc, i) => acc + (Number(i.valueOverTime[idx].value) || 0),
        0,
      );
      return { date, value: parseFloat((sum / ungrouped.length).toFixed(2)) };
    });
    topLevelItems.push({
      label: 'Other',
      valueOverTime: otherVot,
      color: isDark ? '#90a4ae' : '#607d8b',
      checked: true,
    });
  }

  return topLevelItems;
};

const calculateGroupScore = (
  children: GroupedCategoryScore[],
): number | 'N/A' => {
  const validScores = children
    .map(child => child.score)
    .filter((score): score is number => typeof score === 'number');

  if (validScores.length === 0) return 'N/A';
  return Number(
    (
      validScores.reduce((acc, score) => acc + score, 0) / validScores.length
    ).toFixed(2),
  );
};

const calculateGroupTier = (score: number | 'N/A'): string => {
  if (score === 'N/A') return 'N/A';
  if (score < 50) return 'CRITICAL';
  if (score < 75) return 'CAUTION';
  return 'ON_TRACK';
};

const buildHierarchyWithScores = (
  node: HierarchyNode,
  categoryScoresMap: Map<string, CategoryScore>,
): GroupedCategoryScore => {
  if (node.type === 'category' && node.categoryName) {
    const categoryData = categoryScoresMap.get(node.categoryName);
    return {
      name: node.name,
      displayName: node.displayName,
      type: 'category',
      score: categoryData?.score ?? 'N/A',
      tier: categoryData?.tier ?? 'N/A',
      categoryData,
    };
  }

  const children =
    node.children?.map(child =>
      buildHierarchyWithScores(child, categoryScoresMap),
    ) ?? [];
  const score = calculateGroupScore(children);
  return {
    name: node.name,
    displayName: node.displayName,
    type: 'group',
    score,
    tier: calculateGroupTier(score),
    children,
  };
};

const compareScores = (a: CategoryScore, b: CategoryScore): number => {
  if (a.score === 'N/A') return 1;
  if (b.score === 'N/A') return -1;
  return (a.score as number) - (b.score as number);
};

export const buildScorecardHierarchy = (
  categoryScores: CategoryScore[],
): GroupedCategoryScore[] => {
  const categoryScoresMap = new Map<string, CategoryScore>(
    categoryScores.map(cat => [cat.category, cat]),
  );
  const hierarchyGroups = SCORECARD_HIERARCHY.map(rootNode =>
    buildHierarchyWithScores(rootNode, categoryScoresMap),
  );

  const categoriesInHierarchy = getCategoriesInHierarchy();
  const ungrouped = categoryScores.filter(
    cat => !categoriesInHierarchy.includes(cat.category),
  );

  if (ungrouped.length > 0) {
    const children: GroupedCategoryScore[] = ungrouped.map(cat => ({
      name: cat.category.toLowerCase().replace(/\s+/g, '_'),
      displayName: cat.category,
      type: 'category',
      score: cat.score,
      tier: cat.tier,
      categoryData: cat,
    }));
    const score = calculateGroupScore(children);
    hierarchyGroups.push({
      name: 'other',
      displayName: 'Other',
      type: 'group',
      score,
      tier: calculateGroupTier(score),
      children,
    });
  }
  return hierarchyGroups;
};

export const flattenHierarchy = (
  groupedScores: GroupedCategoryScore[],
): CategoryScore[] => {
  const result: CategoryScore[] = [];
  const traverse = (node: GroupedCategoryScore) => {
    if (node.type === 'category' && node.categoryData)
      result.push(node.categoryData);
    node.children?.forEach(traverse);
  };
  groupedScores.forEach(traverse);
  return result;
};

export const getCategoriesNeedingFocus = (
  groupedScores: GroupedCategoryScore[],
): CategoryScore[] =>
  flattenHierarchy(groupedScores)
    .filter(cat => cat.tier === 'CRITICAL' || cat.tier === 'CAUTION')
    .sort(compareScores);

export const getCategoriesPerformingWell = (
  groupedScores: GroupedCategoryScore[],
): CategoryScore[] =>
  flattenHierarchy(groupedScores)
    .filter(
      cat =>
        cat.score !== 'N/A' &&
        cat.tier !== 'CRITICAL' &&
        cat.tier !== 'CAUTION',
    )
    .sort(compareScores);

export const getCategoriesNoData = (
  groupedScores: GroupedCategoryScore[],
): CategoryScore[] =>
  flattenHierarchy(groupedScores)
    .filter(cat => cat.score === 'N/A' && !cat.failed)
    .sort((a, b) => a.category.localeCompare(b.category));

export const getCategoriesFailed = (
  groupedScores: GroupedCategoryScore[],
): CategoryScore[] =>
  flattenHierarchy(groupedScores)
    .filter(cat => Boolean(cat.failed))
    .sort((a, b) => a.category.localeCompare(b.category));
